// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import tippy, { Instance } from 'tippy.js';
import { FeedbackResult } from '../../tools/feedback/feedbackmanager';

class FeedbackButtonComponent extends GirafeHTMLElement {
  static readonly observedAttributes: string[] = ['question', 'component-name', 'placement', 'margins'];
  templateUrl = './template.html';
  styleUrls = ['./style.css', '../../styles/common.css'];

  question!: string;
  componentName?: string;
  placement?: string;

  verticalMargin: string = '0px';
  horizontalMargin: string = '0px';

  tippyInstance: Instance | undefined;

  public constructor() {
    super('feedbackbutton');
    this.displayStyle = 'block';
  }

  public async positiveFeedback() {
    await this.context.feedbackManager.giveFeedback(this.componentName!, FeedbackResult.POSITIVE);
    this.showThankYouPopup();
  }

  public async negativeFeedback() {
    await this.context.feedbackManager.giveFeedback(this.componentName!, FeedbackResult.NEGATIVE);
    this.showThankYouPopup();
  }

  private showThankYouPopup() {
    super.girafeTranslate();
    const popup = this.shadow.querySelector<HTMLDivElement>('#thankyou-popup')!;
    popup.classList.remove('hidden');
    popup.classList.add('thankyou-popup');
    this.tippyInstance?.setProps({
      animation: 'scale'
    });
    this.tippyInstance?.setContent(popup);
    globalThis.setTimeout(() => {
      this.tippyInstance?.hide();
    }, 2000);
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  protected override disconnectedCallback() {
    super.disconnectedCallback();
  }

  override render() {
    super.render();
    this.question = this.getAttribute('question') ?? 'feedback-question-default';
    this.componentName = this.getAttribute('component-name') ?? undefined;
    this.placement = this.getAttribute('placement') ?? undefined;
    this.calculateMargins(this.getAttribute('margins'));
    this.calculatePlacement();
    this.createPopupHandler();
    super.girafeTranslate();
    this.refreshRender();
  }

  private calculateMargins(componentMargin: string | null) {
    if (!componentMargin || componentMargin.trim().length === 0) {
      this.verticalMargin = '0px';
      this.horizontalMargin = '0px';
      return;
    }

    const margins = componentMargin.split(' ');
    if (margins.length != 2) {
      throw new Error('Invalid value for margins');
    }
    this.verticalMargin = margins[0];
    this.horizontalMargin = margins[1];
  }

  private calculatePlacement() {
    console.log('calculatePlacement()', this.shadow.parentElement);
    if (this.componentName) {
      const componentsByName = this.context.componentManager.getComponentsByName(this.componentName);
      if (componentsByName.length != 1) {
        console.error(`Multiple components with name ${this.componentName} found.`);
        return;
      }
      const girafeHTMLElement = componentsByName[0];
      if (girafeHTMLElement) {
        const feedbackContainer = this.shadow.querySelector<HTMLDivElement>('#feedback-container');
        if (feedbackContainer) {
          this.setContainerPosition(feedbackContainer);
        }
      }
    }
  }

  private setContainerPosition(feedbackContainer: HTMLDivElement) {
    switch (this.placement) {
      case 'top-left':
        feedbackContainer.style.top = `${this.verticalMargin}`;
        feedbackContainer.style.left = `${this.horizontalMargin}`;
        break;
      case 'top-center':
        feedbackContainer.style.top = `${this.verticalMargin}`;
        feedbackContainer.style.left = `calc(50% - ${feedbackContainer.clientWidth / 2}px)`;
        break;
      case 'top-right':
        feedbackContainer.style.top = `${this.verticalMargin}`;
        feedbackContainer.style.right = `${this.horizontalMargin}`;
        break;

      case 'center-left':
        feedbackContainer.style.top = `calc(50% - ${feedbackContainer.clientHeight / 2}px)`;
        feedbackContainer.style.left = `${this.horizontalMargin}`;
        break;
      case 'center-center':
        feedbackContainer.style.top = `calc(50% - ${feedbackContainer.clientHeight / 2}px)`;
        feedbackContainer.style.left = `calc(50% - ${feedbackContainer.clientWidth / 2}px)`;
        break;
      case 'center-right':
        feedbackContainer.style.top = `calc(50% - ${feedbackContainer.clientHeight / 2}px)`;
        feedbackContainer.style.right = `${this.horizontalMargin}`;
        break;

      case 'bottom-left':
        feedbackContainer.style.bottom = `${this.verticalMargin}`;
        feedbackContainer.style.left = `${this.horizontalMargin}`;
        break;
      case 'bottom-center':
        feedbackContainer.style.bottom = `${this.verticalMargin}`;
        feedbackContainer.style.left = `calc(50% - ${feedbackContainer.clientWidth / 2}px)`;
        break;
      case 'bottom-right':
      default:
        feedbackContainer.style.bottom = `${this.verticalMargin}`;
        feedbackContainer.style.right = `${this.horizontalMargin}`;
        break;
    }
  }

  private createPopupHandler() {
    const button = this.shadow.querySelector<HTMLButtonElement>('#feedback-button')!;
    this.tippyInstance = tippy(button, {
      trigger: 'click',
      arrow: true,
      interactive: true,
      theme: 'light',
      placement: this.getPopupPlacement(),
      content: (_ref: object) => {
        const popup = this.shadow.querySelector<HTMLDivElement>('#feedback-popup')!;
        this.context.i18nManager.translate(popup);
        popup.classList.remove('hidden');
        popup.classList.add('feedback-popup');
        return popup;
      }
    });
  }

  private getPopupPlacement() {
    switch (this.placement) {
      case 'top-left':
        return 'bottom-start';
      case 'top-center':
        return 'bottom';
      case 'top-right':
        return 'bottom-end';

      case 'center-left':
        return 'left-start';
      case 'center-center':
        return 'bottom';
      case 'center-right':
        return 'left-end';

      case 'bottom-left':
        return 'top-start';
      case 'bottom-center':
        return 'top';
      case 'bottom-right':
        return 'top-end';
    }
    return 'top-end';
  }

  protected attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    if (name === 'question') {
      this.question = newValue;
    } else if (name === 'component-name') {
      this.componentName = newValue;
    } else if (name === 'placement') {
      this.placement = newValue;
    } else if (name === 'margin') {
      this.calculateMargins(newValue);
    }
  }
}

export default FeedbackButtonComponent;
